import { useEffect, useMemo, useState } from 'react';
import { usePMContext } from '../../contexts/PMContext';
import { useUserContext } from '../../contexts/UserContext';
import { computeStatusCounts, type EnrolledUnitForStatus } from '../../utils/pmFilterUtils';
import { pmSchedulingUM } from '../pages-equipment/equipmentFieldUtils';
import { getNextServiceInfo, getPrimaryServiceLevel, serviceLevelsOnSchedulingScale } from '../../utils/pmUtils';
import { SingleUnit } from '../../types/schema';
import { fetchPMReportsByCompany } from '../../graphql/pmQueries';
import { IconAlertTriangle, IconCheckCircle, IconClose, StatsGrid } from '../ui';
import '../../css/PMDashboard.css';

/**
 * Combined PM status summary: Overdue, Due Soon, Scheduled/OK, Completed This Month.
 * Counts are computed from enrolled units using computeStatusCounts.
 *
 * Validates: Requirement 7.1
 */
export const PMStatusCards = () => {
    const { pmConfigurations, enrolledUnits } = usePMContext();
    const { companyUser, companyOwner } = useUserContext();
    const [completedThisMonth, setCompletedThisMonth] = useState(0);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';

    const statusCounts = useMemo(() => {
        const entries: EnrolledUnitForStatus[] = [];

        for (const unit of enrolledUnits as SingleUnit[]) {
            const config = pmConfigurations.find((c) => c.unitId === unit.id);
            if (!config) continue;

            const currentUM = pmSchedulingUM(unit);
            const dueSoonPct = config.dueSoonPercentage ?? 0.1;
            const serviceLevels = serviceLevelsOnSchedulingScale(
                config.serviceLevels ?? [],
                unit.replacedUMCounterValue,
            );

            if (serviceLevels.length === 0) {
                entries.push({ currentUM, targetUM: currentUM + 1, dueSoonPercentage: dueSoonPct });
                continue;
            }

            const nextService = getNextServiceInfo(currentUM, serviceLevels);

            entries.push({
                currentUM,
                targetUM: nextService?.targetUM ?? currentUM + 1,
                dueSoonPercentage: dueSoonPct,
                intervalUM: getPrimaryServiceLevel(serviceLevels)?.intervalValue,
            });
        }

        return computeStatusCounts(entries);
    }, [enrolledUnits, pmConfigurations]);

    useEffect(() => {
        if (!companyId) {
            setCompletedThisMonth(0);
            return;
        }

        let isCancelled = false;
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();

        (async () => {
            try {
                const reports = await fetchPMReportsByCompany(companyId, {
                    between: [monthStart, monthEnd],
                });
                if (isCancelled) return;
                setCompletedThisMonth(reports.length);
            } catch {
                if (isCancelled) return;
                setCompletedThisMonth(0);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    const stats = [
        {
            key: 'overdue',
            itemClass: 'pm-stat-overdue',
            value: `${statusCounts.overdue.toLocaleString()} units`,
            label: 'Overdue Services',
            icon: (
                <span className="pm-stat-icon-x">
                    <IconClose />
                </span>
            ),
        },
        {
            key: 'due-soon',
            itemClass: 'pm-stat-due-soon',
            value: `${statusCounts.dueSoon.toLocaleString()} units`,
            label: 'Due Soon',
            icon: <IconAlertTriangle />,
        },
        {
            key: 'scheduled',
            itemClass: 'pm-stat-scheduled',
            value: `${statusCounts.scheduled.toLocaleString()} units`,
            label: 'Scheduled / OK',
            icon: <IconCheckCircle />,
        },
        {
            key: 'completed',
            itemClass: 'pm-stat-completed',
            value: `${completedThisMonth.toLocaleString()} services`,
            label: 'Completed This Month',
            icon: <IconCheckCircle />,
        },
    ];

    return (
        <StatsGrid
            className="pm-status-cards"
            stats={stats.map((card) => (
                <div key={card.key} className={`stat-item ${card.itemClass}`}>
                    <div className="stat-row">
                        <div className="stat-icon" aria-hidden="true">
                            {card.icon}
                        </div>
                        <div className="stat-value">{card.value}</div>
                    </div>
                    <div className="stat-row">
                        <div className="stat-label">{card.label}</div>
                    </div>
                </div>
            ))}
        />
    );
};
